import { PDFDocument, rgb, StandardFonts } from 'pdf-lib'
import QRCode from 'qrcode'
import {
  normalizeLeadingInteger,
  normalizeMaterialJpl,
} from './numberNormalizer'

export interface CertificateConfig {
  name: string
  x?: number // Optional: if not provided, text will be centered
  y: number
  courseName?: string
  courseX?: number // Optional: if not provided, text will be centered
  courseY?: number
  fontSize?: number
  fontColor?: { r: number; g: number; b: number }
  fontFamily?: string
  courseFontSize?: number
  courseFontColor?: { r: number; g: number; b: number }
  enableTextWrapping?: boolean // Enable auto text wrapping for long text
  maxTextWidth?: number // Maximum width for text (percentage of page width, default: 80)
  lineHeight?: number // Line height multiplier (default: 1.2)
  topics?: Array<{ name: string }> // Optional: list of course topics to display in table
  materials?: Array<{ topic: string; jpl?: string; speaker?: string }>
  materialTopicOnly?: boolean
  certificateNumber?: string
  certificateNumberX?: number
  certificateNumberY?: number
  eventDates?: string
  eventDatesX?: number
  eventDatesY?: number
  jplTotal?: string
  jplTotalX?: number
  jplTotalY?: number
  speakers?: string
  speakersX?: number
  speakersY?: number
  jplNote?: string
  jplNoteX?: number
  jplNoteY?: number
  evaluationScore?: string
  evaluationPassed?: boolean
  evaluationPassingGrade?: string
  evaluationX?: number
  evaluationY?: number
  qrValue?: string
  qrX?: number
  qrY?: number
  qrSize?: number
  signature?: {
    signatureImageUrl?: string // URL to signature image asset
    location?: string // Location text (default: "Bandar Lampung")
    date?: string // Date string (if not provided, will use current date)
    marginBottom?: number // Distance from bottom in pixels (default: 64)
    fontSize?: number // Font size for location and date text (default: 12)
    fontColor?: { r: number; g: number; b: number } // Font color (default: black)
    imageWidth?: number // Signature image width (default: 200)
    imageHeight?: number // Signature image height (default: 100)
    organizerText?: string // Organizer line (default: PT. Jadi Praktisi Indonesia)
    showOrganizerText?: boolean // Toggle organizer line (default: true)
    signerName?: string // Signer name for blanko layout
    signerTitle?: string // Signer title for blanko layout
  },

  // Cbt Needs
  cbtScore?: string,
  cbtScoreX?: number,
  cbtScoreY?: number,
  cbtIsPassed?: boolean,
  cbtPassingGrade?: string
  cbtPassingGradeX?: number
  cbtPassingGradeY?: number
}

export interface CertificateTemplate {
  templateUrl: string
  namePosition: {
    x?: number // Optional: if not provided, text will be centered
    y: number
  }
  coursePosition?: {
    x?: number // Optional: if not provided, text will be centered
    y: number
  }
  defaultFontSize?: number
  defaultFontColor?: { r: number; g: number; b: number }
  defaultCourseFontSize?: number
  defaultCourseFontColor?: { r: number; g: number; b: number }
  layout?: 'blanko-v1' | 'legacy'
  branding?: {
    logoUrl?: string
    logoWidth?: number
    logoTopMargin?: number
    stampUrl?: string
    stampWidth?: number
    stampOffsetX?: number
    stampOffsetY?: number
  }
}

const normalizeTopicRows = (
  topics?: CertificateConfig['topics'],
): Array<{ topic: string }> | undefined => {
  if (!Array.isArray(topics)) return undefined

  const rows = topics
    .map((topic: any) => ({
      topic:
        typeof topic === 'string'
          ? topic
          : topic?.name || topic?.topic || topic?.title || '',
    }))
    .filter((topic) => topic.topic)

  return rows.length ? rows : undefined
}

const normalizeMaterialRows = (
  materials?: CertificateConfig['materials'] | Record<string, any> | string,
): Array<{ topic: string; jpl?: string; speaker?: string }> | undefined => {
  let rows: any[] = []

  if (Array.isArray(materials)) {
    rows = materials
  } else if (typeof materials === 'string') {
    try {
      const parsed = JSON.parse(materials)
      rows = Array.isArray(parsed)
        ? parsed
        : parsed && typeof parsed === 'object'
        ? Object.values(parsed)
        : []
    } catch {
      rows = materials
        .split(',')
        .map((topic) => ({ topic: topic.trim() }))
        .filter((item) => item.topic)
    }
  } else if (materials && typeof materials === 'object') {
    rows = Object.values(materials)
  }

  const normalizedRows = rows
    .map((row: any) => ({
      topic:
        typeof row === 'string'
          ? row
          : row?.topic || row?.name || row?.title || '',
      jpl:
        typeof row === 'string'
          ? ''
          : normalizeMaterialJpl(row?.jpl || row?.duration || ''),
      speaker: typeof row === 'string' ? '' : row?.speaker || '',
    }))
    .filter((row) => row.topic || row.jpl || row.speaker)

  return normalizedRows.length ? normalizedRows : undefined
}

// Default template configuration
/**
 * Split text into lines that fit within the specified width
 */
const splitTextIntoLines = (
  text: string,
  font: any,
  fontSize: number,
  maxWidth: number,
): string[] => {
  const words = text.split(' ')
  const lines: string[] = []
  let currentLine = ''

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word
    const testWidth = font.widthOfTextAtSize(testLine, fontSize)

    if (testWidth <= maxWidth) {
      currentLine = testLine
    } else {
      if (currentLine) {
        lines.push(currentLine)
        currentLine = word
      } else {
        // If a single word is too long, split it
        lines.push(word)
      }
    }
  }

  if (currentLine) {
    lines.push(currentLine)
  }

  return lines
}

/**
 * Draw multi-line text with center alignment
 */
const drawMultiLineText = (
  page: any,
  text: string,
  font: any,
  fontSize: number,
  x: number,
  y: number,
  maxWidth: number,
  color: any,
  lineHeight: number = 1.2,
): number => {
  const lines = splitTextIntoLines(text, font, fontSize, maxWidth)
  const totalHeight = lines.length * fontSize * lineHeight

  // Calculate starting Y position to center the entire text block
  const startY = y + (totalHeight - fontSize) / 2

  lines.forEach((line, index) => {
    const lineWidth = font.widthOfTextAtSize(line, fontSize)
    const lineX = x + (maxWidth - lineWidth) / 2 // Center each line
    const lineY = startY - index * fontSize * lineHeight

    page.drawText(line, {
      x: lineX,
      y: lineY,
      size: fontSize,
      font,
      color,
    })
  })

  return totalHeight
}

const drawLeftAlignedMultiLineText = (
  page: any,
  text: string,
  font: any,
  fontSize: number,
  x: number,
  y: number,
  maxWidth: number,
  color: any,
  lineHeight: number = 1.2,
): number => {
  const lines = splitTextIntoLines(text, font, fontSize, maxWidth)

  lines.forEach((line, index) => {
    page.drawText(line, {
      x,
      y: y - index * fontSize * lineHeight,
      size: fontSize,
      font,
      color,
    })
  })

  return lines.length * fontSize * lineHeight
}

/**
 * Draw topics table on the certificate
 */
const drawTopicsTable = async (
  page: any,
  materials: Array<{ topic: string; jpl?: string; speaker?: string }>,
  font: any,
  fontBold: any,
  x: number,
  y: number,
  width: number,
  fontSize: number = 12,
  jplTotal?: string,
  topicOnly: boolean = false,
): Promise<number> => {
  const titleFontSize = fontSize + 1
  const titleText = 'List of Material Studied'
  const titleHeight = titleFontSize + 9
  const tableHeaderHeight = fontSize * 1.8
  const paddingX = fontSize <= 9 ? 7 : 9
  const paddingY = fontSize <= 9 ? 4 : 5
  const headerColor = rgb(0.0, 0.63, 0.86)
  const headerTextColor = rgb(1, 1, 1)
  const textColor = rgb(0, 0, 0)
  const borderColor = rgb(0.8, 0.85, 0.9)
  const zebraColor = rgb(0.92, 0.97, 1.0)

  const titleY = y - titleHeight + 2
  page.drawText(titleText, {
    x,
    y: titleY,
    size: titleFontSize,
    font: fontBold,
    color: textColor,
  })

  const headerY = y - titleHeight - tableHeaderHeight
  page.drawRectangle({
    x,
    y: headerY,
    width,
    height: tableHeaderHeight,
    color: headerColor,
  })

  const columnWidths = {
    no: width * 0.08,
    topic: topicOnly ? width * 0.92 : width * 0.43,
    jpl: topicOnly ? 0 : width * 0.21,
    speaker: topicOnly ? 0 : width * 0.28,
  }

  const headerTextY = headerY + (tableHeaderHeight - fontSize) / 2 + 1
  page.drawText('No', {
    x: x + (columnWidths.no - fontBold.widthOfTextAtSize('No', fontSize)) / 2,
    y: headerTextY,
    size: fontSize,
    font: fontBold,
    color: headerTextColor,
  })
  page.drawText('Topic', {
    x: x + columnWidths.no + paddingX,
    y: headerTextY,
    size: fontSize,
    font: fontBold,
    color: headerTextColor,
  })
  if (!topicOnly) {
    const jplHeader = 'Time Studied (JPL)*'
    const jplHeaderSize = Math.min(
      fontSize - 1,
      Math.max(6, (columnWidths.jpl - paddingX * 2) / Math.max(jplHeader.length, 1)),
    )
    page.drawText(jplHeader, {
      x:
        x +
        columnWidths.no +
        columnWidths.topic +
        (columnWidths.jpl - fontBold.widthOfTextAtSize(jplHeader, jplHeaderSize)) / 2,
      y: headerTextY,
      size: jplHeaderSize,
      font: fontBold,
      color: headerTextColor,
    })
    page.drawText('Speaker', {
      x:
        x +
        columnWidths.no +
        columnWidths.topic +
        columnWidths.jpl +
        paddingX,
      y: headerTextY,
      size: fontSize,
      font: fontBold,
      color: headerTextColor,
    })
  }

  let currentY = headerY
  materials.forEach((row, index) => {
    const topicText = row.topic || ''
    const topicLines = wrapTextToLines(
      topicText,
      font,
      fontSize,
      columnWidths.topic - paddingX * 2,
    ).slice(0, 2)
    const speakerText = topicOnly ? '' : row.speaker || ''
    const speakerLines = topicOnly
      ? []
      : wrapTextToLines(
          speakerText,
          font,
          fontSize,
          columnWidths.speaker - paddingX * 2,
        ).slice(0, 2)
    const rowLines = Math.max(topicLines.length, speakerLines.length, 1)
    const rowHeight = rowLines * (fontSize + 2) + paddingY * 2
    const rowY = currentY - rowHeight

    if (index % 2 === 0) {
      page.drawRectangle({
        x,
        y: rowY,
        width,
        height: rowHeight,
        color: zebraColor,
      })
    }

    const rowTextY = rowY + rowHeight - paddingY - fontSize
    const noText = String(index + 1)
    page.drawText(noText, {
      x: x + (columnWidths.no - font.widthOfTextAtSize(noText, fontSize)) / 2,
      y: rowTextY,
      size: fontSize,
      font,
      color: textColor,
    })

    topicLines.forEach((line, lineIndex) => {
      page.drawText(line, {
        x: x + columnWidths.no + paddingX,
        y: rowTextY - lineIndex * (fontSize + 2),
        size: fontSize,
        font,
        color: textColor,
      })
    })

    if (!topicOnly) {
      const jplText = row.jpl || ''
      page.drawText(jplText, {
        x:
          x +
          columnWidths.no +
          columnWidths.topic +
          (columnWidths.jpl - font.widthOfTextAtSize(jplText, fontSize)) / 2,
        y: rowTextY,
        size: fontSize,
        font,
        color: textColor,
      })

      speakerLines.forEach((line, lineIndex) => {
        page.drawText(line, {
          x: x + columnWidths.no + columnWidths.topic + columnWidths.jpl + paddingX,
          y: rowTextY - lineIndex * (fontSize + 2),
          size: fontSize,
          font,
          color: textColor,
        })
      })
    }

    currentY = rowY
  })

  const normalizedJplTotal = normalizeLeadingInteger(jplTotal || '')

  if (normalizedJplTotal && !topicOnly) {
    const totalRowHeight = fontSize * 1.8
    const totalRowY = currentY - totalRowHeight
    page.drawRectangle({
      x,
      y: totalRowY,
      width,
      height: totalRowHeight,
      color: rgb(0.9, 0.96, 1.0),
    })

    const totalTextY = totalRowY + (totalRowHeight - fontSize) / 2 + 1
    page.drawText('Total', {
      x:
        x +
        columnWidths.no +
        (columnWidths.topic - fontBold.widthOfTextAtSize('Total', fontSize)) / 2,
      y: totalTextY,
      size: fontSize,
      font: fontBold,
      color: textColor,
    })
    page.drawText(normalizedJplTotal, {
      x:
        x +
        columnWidths.no +
        columnWidths.topic +
        (columnWidths.jpl -
          fontBold.widthOfTextAtSize(normalizedJplTotal, fontSize)) /
          2,
      y: totalTextY,
      size: fontSize,
      font: fontBold,
      color: textColor,
    })

    currentY = totalRowY
  }

  const borderedTableHeight = y - titleHeight - currentY
  const totalHeight = titleHeight + borderedTableHeight
  page.drawRectangle({
    x,
    y: currentY,
    width,
    height: borderedTableHeight,
    borderColor,
    borderWidth: 1,
  })

  return totalHeight
}

const assetBytesCache = new Map<string, Promise<ArrayBuffer>>()
const qrPngBytesCache = new Map<string, Promise<ArrayBuffer>>()

const cloneArrayBuffer = (buffer: ArrayBuffer): ArrayBuffer => buffer.slice(0)

const dataUrlToArrayBuffer = (dataUrl: string): ArrayBuffer => {
  const base64 = dataUrl.split(',')[1] || ''
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index)
  }

  return bytes.buffer
}

const drawQrCode = async (
  page: any,
  pdfDoc: any,
  qrValue: string,
  x: number,
  y: number,
  size: number,
): Promise<void> => {
  try {
    const roundedSize = Math.round(size)
    const cacheKey = `${qrValue}|${roundedSize}`
    let cachedQrBytes = qrPngBytesCache.get(cacheKey)

    if (!cachedQrBytes) {
      cachedQrBytes = QRCode.toDataURL(qrValue, {
        margin: 1,
        width: roundedSize,
      })
        .then(dataUrlToArrayBuffer)
        .catch((error) => {
          qrPngBytesCache.delete(cacheKey)
          throw error
        })
      qrPngBytesCache.set(cacheKey, cachedQrBytes)
    }

    const arrayBuffer = cloneArrayBuffer(await cachedQrBytes)
    const qrImage = await pdfDoc.embedPng(arrayBuffer)

    page.drawImage(qrImage, {
      x,
      y,
      width: size,
      height: size,
    })
  } catch (error) {
    console.warn('Failed to generate QR code:', error)
  }
}

const A4_PORTRAIT = { width: 595, height: 842 }

const isPdfTemplate = (templateUrl: string): boolean => {
  const normalized = templateUrl.split('?')[0].toLowerCase()
  return normalized.endsWith('.pdf')
}

const fetchAssetBytes = async (assetUrl: string): Promise<ArrayBuffer> => {
  const encodedUrl = encodeURI(assetUrl)
  let cachedBytes = assetBytesCache.get(encodedUrl)

  if (!cachedBytes) {
    cachedBytes = fetch(encodedUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch asset: ${assetUrl}`)
        }

        return response.arrayBuffer()
      })
      .catch((error) => {
        assetBytesCache.delete(encodedUrl)
        throw error
      })
    assetBytesCache.set(encodedUrl, cachedBytes)
  }

  return cloneArrayBuffer(await cachedBytes)
}

const embedImageFromUrl = async (pdfDoc: any, imageUrl: string) => {
  const bytes = await fetchAssetBytes(imageUrl)
  const normalized = imageUrl.split('?')[0].toLowerCase()

  if (normalized.endsWith('.png')) {
    return pdfDoc.embedPng(bytes)
  }

  if (normalized.endsWith('.jpg') || normalized.endsWith('.jpeg')) {
    return pdfDoc.embedJpg(bytes)
  }

  throw new Error(`Unsupported image format: ${imageUrl}`)
}

const drawImageCover = (
  page: any,
  image: any,
  pageWidth: number,
  pageHeight: number,
): void => {
  const scale = Math.max(pageWidth / image.width, pageHeight / image.height)
  const drawWidth = image.width * scale
  const drawHeight = image.height * scale
  const x = (pageWidth - drawWidth) / 2
  const y = (pageHeight - drawHeight) / 2

  page.drawImage(image, {
    x,
    y,
    width: drawWidth,
    height: drawHeight,
  })
}

const drawBranding = async (
  page: any,
  pdfDoc: any,
  pageWidth: number,
  pageHeight: number,
  branding: NonNullable<CertificateTemplate['branding']>,
  qrPlacement?: { x: number; y: number; size: number },
): Promise<void> => {
  if (branding.logoUrl) {
    try {
      const logoImage = await embedImageFromUrl(pdfDoc, branding.logoUrl)
      const logoWidth = branding.logoWidth || 180
      const logoHeight = logoImage.height * (logoWidth / logoImage.width)
      const logoX = (pageWidth - logoWidth) / 2
      const logoTopMargin = branding.logoTopMargin || 56
      const logoY = pageHeight - logoTopMargin - logoHeight

      page.drawImage(logoImage, {
        x: logoX,
        y: logoY,
        width: logoWidth,
        height: logoHeight,
      })
    } catch (error) {
      console.warn('Failed to draw logo branding:', error)
    }
  }

  if (branding.stampUrl && qrPlacement) {
    try {
      const stampImage = await embedImageFromUrl(pdfDoc, branding.stampUrl)
      const stampWidth = branding.stampWidth || 70
      const stampHeight = stampImage.height * (stampWidth / stampImage.width)
      const offsetX = branding.stampOffsetX ?? 16
      const offsetY = branding.stampOffsetY ?? 0
      const stampX = qrPlacement.x + qrPlacement.size + offsetX
      const stampY = qrPlacement.y + (qrPlacement.size - stampHeight) / 2 + offsetY

      page.drawImage(stampImage, {
        x: stampX,
        y: stampY,
        width: stampWidth,
        height: stampHeight,
      })
    } catch (error) {
      console.warn('Failed to draw stamp branding:', error)
    }
  }
}

const formatEnglishDate = (date: Date): string => {
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]

  const day = date.getDate()
  const monthName = months[date.getMonth()]
  const year = date.getFullYear()

  return `${day} ${monthName} ${year}`
}

const resolveSignatureDate = (dateInput?: string): string => {
  if (!dateInput) return formatEnglishDate(new Date())
  const parsed = new Date(dateInput)
  if (!Number.isNaN(parsed.getTime())) {
    return formatEnglishDate(parsed)
  }
  return dateInput
}

const DIGITAL_SIGNATURE_PADDING = 7
const DIGITAL_SIGNATURE_TEXT_GAP = 8
const DIGITAL_SIGNATURE_TEXT_WIDTH = 74

const drawQrBox = (
  page: any,
  font: any,
  qrPlacement: { x: number; y: number; size: number },
  stampWidth: number,
  stampOffsetX: number,
): void => {
  const padding = DIGITAL_SIGNATURE_PADDING
  const boxX = qrPlacement.x - padding
  const boxY = qrPlacement.y - padding
  const boxWidth =
    qrPlacement.size +
    stampOffsetX +
    stampWidth +
    DIGITAL_SIGNATURE_TEXT_GAP +
    DIGITAL_SIGNATURE_TEXT_WIDTH +
    padding * 2
  const boxHeight = qrPlacement.size + padding * 2
  const rightPanelX =
    qrPlacement.x +
    qrPlacement.size +
    stampOffsetX +
    stampWidth +
    DIGITAL_SIGNATURE_TEXT_GAP
  const rightPanelWidth = DIGITAL_SIGNATURE_TEXT_WIDTH

  page.drawRectangle({
    x: boxX,
    y: boxY,
    width: boxWidth,
    height: boxHeight,
    borderColor: rgb(0.75, 0.8, 0.85),
    borderWidth: 1,
  })

  const textLines = ['This certificate is', 'digitally signed']
  const lineHeight = 8.5
  const textBlockHeight = textLines.length * lineHeight
  const textY = boxY + (boxHeight - textBlockHeight) / 2 + 1
  textLines.forEach((line, index) => {
    const fontSize = 6.8
    const textWidth = font.widthOfTextAtSize(line, fontSize)
    const textX = rightPanelX + Math.max(0, (rightPanelWidth - textWidth) / 2)
    page.drawText(line, {
      x: textX,
      y: textY + (textLines.length - 1 - index) * lineHeight,
      size: fontSize,
      font,
      color: rgb(0.2, 0.2, 0.2),
    })
  })
}

/**
 * Draw signature section at the bottom of the certificate
 */
const drawSignature = async (
  page: any,
  pdfDoc: any,
  signature: NonNullable<CertificateConfig['signature']>,
  pageWidth: number,
  pageHeight: number,
  font: any,
): Promise<void> => {
  const {
    signatureImageUrl,
    location = 'Bandar Lampung',
    date,
    marginBottom = 64,
    fontSize = 12,
    fontColor = { r: 0, g: 0, b: 0 }, // Default black
    imageWidth = 200,
    imageHeight = 100,
    organizerText = 'Organized by PT. Jadi Praktisi Indonesia',
    showOrganizerText = true,
  } = signature

  // Use provided date or format current date
  const dateText = resolveSignatureDate(date)
  const locationDateText = `${location}, ${dateText}`

  // Calculate positions - text should be above the image
  const signatureImageY = marginBottom // Image distance from bottom
  const textY = signatureImageY + imageHeight + 20 // Location/date text above the signature
  const organizerTextY = textY + fontSize + 10 // Organizer text above location/date text

  // Center positions
  const signatureX = (pageWidth - imageWidth) / 2
  const textWidth = font.widthOfTextAtSize(locationDateText, fontSize)
  const textX = (pageWidth - textWidth) / 2

  // Embed bold font for organizer text
  const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold)

  // 🟩 Draw organizer label (bold, black, centered)
  if (showOrganizerText) {
    const organizerTextWidth = fontBold.widthOfTextAtSize(organizerText, fontSize + 2)
    const organizerTextX = (pageWidth - organizerTextWidth) / 2
    page.drawText(organizerText, {
      x: organizerTextX,
      y: organizerTextY,
      size: fontSize + 2,
      font: fontBold,
      color: rgb(0, 0, 0), // Black
    })
  }

  // 🟨 Draw location and date text
  page.drawText(locationDateText, {
    x: textX,
    y: textY,
    size: fontSize,
    font,
    color: rgb(fontColor.r / 255, fontColor.g / 255, fontColor.b / 255),
  })

  // 🟦 Draw signature image if provided
  if (signatureImageUrl) {
    try {
      const image = await embedImageFromUrl(pdfDoc, signatureImageUrl)

      page.drawImage(image, {
        x: signatureX,
        y: signatureImageY,
        width: imageWidth,
        height: imageHeight,
      })
    } catch (error) {
      console.warn('Error loading signature image:', error)
    }
  }
}

export const DEFAULT_CERTIFICATE_TEMPLATE: CertificateTemplate = {
  templateUrl: '/assets/certificates/blanko.jpeg',
  namePosition: {
    y: 255,
  },
  coursePosition: {
    y: 340,
  },
  defaultFontSize: 20,
  defaultFontColor: { r: 43, g: 55, b: 82 },
  defaultCourseFontSize: 18,
  defaultCourseFontColor: { r: 43, g: 55, b: 82 },
  layout: 'blanko-v1',
  branding: {
    logoUrl: undefined,
    stampUrl: '/assets/certificates/logo-jp-stamp.png',
    stampWidth: 62,
    stampOffsetX: 14,
    stampOffsetY: 0,
  },
}

export const DEFAULT_CERTIFICATE_CBT_TEMPLATE: CertificateTemplate = {
  templateUrl: '/assets/certificates/blanko.jpeg',
  namePosition: {
    y: 255,
  },
  coursePosition: {
    y: 340,
  },
  defaultFontSize: 20,
  defaultFontColor: { r: 43, g: 55, b: 82 },
  defaultCourseFontSize: 18,
  defaultCourseFontColor: { r: 43, g: 55, b: 82 },
  layout: 'blanko-v1',
  branding: {
    logoUrl: undefined,
    stampUrl: '/assets/certificates/logo-jp-stamp.png',
    stampWidth: 62,
    stampOffsetX: 14,
    stampOffsetY: 0,
  },
}

function wrapTextToLines(text: string, font: any, fontSize: number, maxWidth: number) {
  const words = text.split(' ')
  const lines: string[] = []
  let currentLine = ''

  for (const word of words) {
    const testLine = currentLine ? currentLine + ' ' + word : word
    const testWidth = font.widthOfTextAtSize(testLine, fontSize)

    if (testWidth <= maxWidth) {
      currentLine = testLine
    } else {
      if (currentLine) lines.push(currentLine)
      currentLine = word
    }
  }

  if (currentLine) lines.push(currentLine)
  return lines
}

function measureLines(text: string, font, fontSize: number, courseMaxWidth) {
  return wrapTextToLines(text, font, fontSize, courseMaxWidth).length
}

/**
 * Generate certificate PDF with custom name and position
 */
export const generateCertificate = async (
  config: CertificateConfig,
  template?: CertificateTemplate,
): Promise<Uint8Array> => {
  try {
    const templateConfig = template || DEFAULT_CERTIFICATE_TEMPLATE

    let pdfDoc
    let page

    if (isPdfTemplate(templateConfig.templateUrl)) {
      const templateArrayBuffer = await fetchAssetBytes(
        templateConfig.templateUrl,
      )

      pdfDoc = await PDFDocument.load(templateArrayBuffer)

      const pages = pdfDoc.getPages()
      if (pages.length === 0) {
        throw new Error('Template PDF has no pages')
      }

      page = pages[0]
    } else {
      pdfDoc = await PDFDocument.create()
      page = pdfDoc.addPage([A4_PORTRAIT.width, A4_PORTRAIT.height])

      const backgroundImage = await embedImageFromUrl(
        pdfDoc,
        templateConfig.templateUrl,
      )
      const { width: pageWidth, height: pageHeight } = page.getSize()

      drawImageCover(page, backgroundImage, pageWidth, pageHeight)
    }

    const { width, height } = page.getSize()

    // Embed the standard font
    const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold)
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica)

    // Set font properties
    const fontSize = config.fontSize || templateConfig.defaultFontSize || 22
    const fontColor = config.fontColor ||
      templateConfig.defaultFontColor || { r: 42, g: 170, b: 250 }

    const isBlankoLayout = templateConfig.layout === 'blanko-v1'
    const defaultQrSize = isBlankoLayout ? 76 : 90
    const qrSize = config.qrSize || defaultQrSize
    const defaultStampWidth = templateConfig.branding?.stampWidth || 54
    const defaultStampOffsetX = templateConfig.branding?.stampOffsetX ?? 12
    const defaultQrPadding = DIGITAL_SIGNATURE_PADDING
    const defaultSignatureBoxWidth =
      qrSize +
      defaultStampOffsetX +
      defaultStampWidth +
      DIGITAL_SIGNATURE_TEXT_GAP +
      DIGITAL_SIGNATURE_TEXT_WIDTH +
      defaultQrPadding * 2
    const defaultQrX = isBlankoLayout
      ? (width - defaultSignatureBoxWidth) / 2 + defaultQrPadding
      : (width - qrSize) / 2
    const qrPlacement = {
      x: config.qrX ?? defaultQrX,
      y: config.qrY ?? (isBlankoLayout ? 92 : 90),
      size: qrSize,
    }

    if (templateConfig.branding) {
      await drawBranding(
        page,
        pdfDoc,
        width,
        height,
        templateConfig.branding,
        !isBlankoLayout && config.qrValue ? qrPlacement : undefined,
      )
    }

    // Check if text wrapping is enabled
    const enableWrapping = config.enableTextWrapping !== false // Default to true
    const maxTextWidthPercent = config.maxTextWidth || 80
    const lineHeight = config.lineHeight || 1.2
    let materialTableBottomY: number | undefined
    let materialTableX: number | undefined
    let materialTableWidth: number | undefined
    let dynamicQrPlacement = { ...qrPlacement }

    if (isBlankoLayout) {
      const labelFontSize = 11
      const labelColor = rgb(0.15, 0.18, 0.25)
      const labelFont = font

      if (config.certificateNumber) {
        const numberText = `Number: ${config.certificateNumber}`
        const numberY = height - (config.certificateNumberY || 208)
        const numberX = (width - labelFont.widthOfTextAtSize(numberText, labelFontSize)) / 2

        page.drawText(numberText, {
          x: numberX,
          y: numberY,
          size: labelFontSize,
          font: labelFont,
          color: labelColor,
        })
      }

      const completedText = 'Has successfully completed'
      const completedY = height - 290
      const completedX = (width - labelFont.widthOfTextAtSize(completedText, labelFontSize)) / 2
      page.drawText(completedText, {
        x: completedX,
        y: completedY,
        size: labelFontSize,
        font: labelFont,
        color: labelColor,
      })

      if (config.eventDates) {
        const trimmedEventDates = config.eventDates
          .trim()
          .replace(/^held\s+on\s+held\s+on/i, 'Held on')
        const eventText = /^held\s+on/i.test(trimmedEventDates)
          ? trimmedEventDates
          : `Held on ${trimmedEventDates}`
        const eventY = height - (config.eventDatesY || 385)
        const eventX = width * 0.12
        drawMultiLineText(
          page,
          eventText,
          font,
          labelFontSize,
          eventX,
          eventY,
          width * 0.76,
          labelColor,
          1.2,
        )
      }

      const organizerText = 'Organized by PT. Jadi Praktisi Indonesia'
      const organizerY = height - 405
      const organizerX = (width - labelFont.widthOfTextAtSize(organizerText, labelFontSize)) / 2
      page.drawText(organizerText, {
        x: organizerX,
        y: organizerY,
        size: labelFontSize,
        font: labelFont,
        color: labelColor,
      })
    }

    if (enableWrapping) {
      // Calculate text area for name (with margins)
      const nameMaxWidth = width * (maxTextWidthPercent / 100)
      const nameX = width * ((100 - maxTextWidthPercent) / 200) // Center the text area
      const nameY = height - (config.y || templateConfig.namePosition.y) // PDF coordinates are bottom-up

      // Add name text to the page with auto-wrapping
      const nameHeight = drawMultiLineText(
        page,
        config.name,
        fontBold,
        fontSize,
        nameX,
        nameY,
        nameMaxWidth,
        rgb(fontColor.r / 255, fontColor.g / 255, fontColor.b / 255),
        lineHeight,
      )

      // Add course name if provided
      if (config.courseName) {
        let courseFontSize =
          config.courseFontSize || templateConfig.defaultCourseFontSize || 18

        const courseFontColor = config.courseFontColor ||
          templateConfig.defaultCourseFontColor || { r: 51, g: 51, b: 51 }

        // Calculate text area for course (with margins)
        const courseMaxWidth = width * (maxTextWidthPercent / 100)
        const courseX = width * ((100 - maxTextWidthPercent) / 200) // Center the text area

        const maxLines = 2

        while (
          measureLines(config.courseName, font, courseFontSize, courseMaxWidth) > maxLines &&
          courseFontSize > 10
        ) {
          courseFontSize -= 1
        }
        
        // Position course - use config.courseY if provided, otherwise calculate from name position
        let courseY: number
        if (config.courseY !== undefined) {
          // If course Y position is explicitly set, use it
          courseY = height - config.courseY // PDF coordinates are bottom-up
        } else if (templateConfig.coursePosition?.y !== undefined) {
          // If template has course Y position, use it
          courseY = height - templateConfig.coursePosition.y // PDF coordinates are bottom-up
        } else {
          // Default: position course below name with some spacing
          const courseSpacing = 40 // Space between name and course
          courseY = nameY - nameHeight - courseSpacing
        }

        // Add course text to the page with auto-wrapping
        const courseHeight = drawMultiLineText(
          page,
          config.courseName,
          fontBold,
          courseFontSize,
          courseX,
          courseY,
          courseMaxWidth,
          rgb(
            courseFontColor.r / 255,
            courseFontColor.g / 255,
            courseFontColor.b / 255,
          ),
          lineHeight,
        )

        // Add topics table if provided
        const materialRows =
          normalizeMaterialRows(config.materials) || normalizeTopicRows(config.topics)

        if (materialRows && materialRows.length > 0) {
          const limitedMaterials = materialRows.slice(0, 6)

          const tableWidthPercent = isBlankoLayout
            ? Math.max(maxTextWidthPercent, 86)
            : maxTextWidthPercent
          const tableWidth = width * (tableWidthPercent / 100);
          const tableX = width * ((100 - tableWidthPercent) / 200); // Center the table
          const tableSpacing = isBlankoLayout ? 30 : -20; // Space between course name and topics table
          const tableY = isBlankoLayout
            ? Math.min(height - 412, Math.max(height - 450, courseY - courseHeight - tableSpacing))
            : courseY - courseHeight - tableSpacing;
          const tableFontSize = isBlankoLayout
            ? limitedMaterials.length >= 6
              ? 9
              : 10
            : 12

          const tableHeight = await drawTopicsTable(
            page,
            limitedMaterials,
            font,
            fontBold,
            tableX,
            tableY,
            tableWidth,
            tableFontSize,
            config.jplTotal,
            config.materialTopicOnly,
          )
          materialTableX = tableX
          materialTableWidth = tableWidth
          materialTableBottomY = tableY - tableHeight
        }
      }

      // CBT Needs
      if (config.cbtScore && !isBlankoLayout) {
        const cbtFontSize = 18
        const cbtFontColor = { r: 0, g: 0, b: 0 } // Black
        const cbtPassingFontColor = { r: 42, g: 170, b: 250 } // Blue (same as courseTitle)
        const cbtFailColor = { r: 255, g: 0, b: 0 } // Red

        // Default positions (if not provided)
        const cbtScoreX = config.cbtScoreX || width / 2 - 100
        const cbtScoreY = height - (config.cbtScoreY || 460)

        const cbtPassingGradeX = config.cbtPassingGradeX || width / 2 - 100
        const cbtPassingGradeY = height - (config.cbtPassingGradeY || 490)

        // 🧮 Draw score
        page.drawText(`${config.cbtScore}`, {
          x: cbtScoreX,
          y: cbtScoreY,
          size: cbtFontSize - 2,
          font: fontBold,
          color: rgb(
            cbtFontColor.r / 255,
            cbtFontColor.g / 255,
            cbtFontColor.b / 255,
          ),
        })

        // 🎯 Draw passing grade (if available)
        if (config.cbtPassingGrade) {
          page.drawText(`${config.cbtPassingGrade}`, {
            x: cbtPassingGradeX,
            y: cbtPassingGradeY,
            size: cbtFontSize - 2,
            font: font,
            color: rgb(
              cbtFontColor.r / 255,
              cbtFontColor.g / 255,
              cbtFontColor.b / 255,
            ),
          })
        }

        // 🟢🟥 Draw result (PASSED / FAILED)
        if (config.cbtIsPassed !== undefined) {
          const resultText = config.cbtIsPassed ? 'PASSED' : 'FAILED'
          const resultColor = config.cbtIsPassed ? cbtPassingFontColor : cbtFailColor
          const resultFontSize = cbtFontSize + 10

          // Measure text width in the current font
          const textWidth = fontBold.widthOfTextAtSize(resultText, resultFontSize)

          // Center horizontally
          const resultX = (width - textWidth) / 2

          page.drawText(resultText, {
            x: resultX,
            y: 415,
            size: resultFontSize,
            font: fontBold,
            color: rgb(
              resultColor.r / 255,
              resultColor.g / 255,
              resultColor.b / 255,
            ),
          })
        }
      }

      if (!isBlankoLayout && config.certificateNumber) {
        const numberText = `Number: ${config.certificateNumber}`
        const numberFontSize = 11
        const numberX =
          config.certificateNumberX !== undefined
            ? config.certificateNumberX
            : (width - font.widthOfTextAtSize(numberText, numberFontSize)) / 2
        const numberY = height - (config.certificateNumberY || 208)

        page.drawText(numberText, {
          x: numberX,
          y: numberY,
          size: numberFontSize,
          font,
          color: rgb(0, 0, 0),
        })
      }

      if (!isBlankoLayout && config.eventDates) {
        const eventText = config.eventDates
        const eventFontSize = 11
        const eventX = config.eventDatesX ?? width * 0.12
        const eventY = height - (config.eventDatesY || 430)
        drawMultiLineText(
          page,
          eventText,
          font,
          eventFontSize,
          eventX,
          eventY,
          width * 0.76,
          rgb(0, 0, 0),
          1.2,
        )
      }

      if (config.jplTotal && !isBlankoLayout) {
        const jplText = `Total JPL: ${config.jplTotal}`
        const jplFontSize = 11
        const jplX = config.jplTotalX ?? width * 0.12
        const jplY = height - (config.jplTotalY || (isBlankoLayout ? 465 : 470))

        page.drawText(jplText, {
          x: jplX,
          y: jplY,
          size: jplFontSize,
          font,
          color: rgb(0, 0, 0),
        })
      }

      if (config.speakers && (!isBlankoLayout || materialTableBottomY === undefined)) {
        const speakerText = `Narasumber: ${config.speakers}`
        const speakerFontSize = 11
        const speakerX = config.speakersX ?? width * 0.12
        const speakerY = height - (config.speakersY || (isBlankoLayout ? 445 : 450))
        drawMultiLineText(
          page,
          speakerText,
          font,
          speakerFontSize,
          speakerX,
          speakerY,
          width * 0.76,
          rgb(0, 0, 0),
          1.2,
        )
      }

      if (config.evaluationScore) {
        const passedText =
          config.evaluationPassed === true
            ? 'Passed'
            : config.evaluationPassed === false
            ? 'Failed'
            : 'Not graded'
        const passingGradeText = config.evaluationPassingGrade
          ? config.evaluationPassingGrade
          : '-'
        const evaluationText = `with a Score of ${config.evaluationScore} (${passedText}) score the passing grade that ${passingGradeText}.`
        const evaluationFontSize = 11
        const evaluationX =
          config.evaluationX ??
          (isBlankoLayout && materialTableX !== undefined ? materialTableX : width * 0.12)
        const evaluationWidth =
          isBlankoLayout && materialTableWidth !== undefined ? materialTableWidth : width * 0.76
        const evaluationY =
          config.evaluationY !== undefined
            ? height - config.evaluationY
            : isBlankoLayout && materialTableBottomY !== undefined
            ? materialTableBottomY - 14
            : height - 505

        drawLeftAlignedMultiLineText(
          page,
          evaluationText,
          font,
          evaluationFontSize,
          evaluationX,
          evaluationY,
          evaluationWidth,
          rgb(0, 0, 0),
          1.2,
        )
      }

      if (config.jplNote) {
        const noteFontSize = 10
        const noteX =
          config.jplNoteX ??
          (isBlankoLayout && materialTableX !== undefined ? materialTableX : width * 0.12)
        const noteWidth =
          isBlankoLayout && materialTableWidth !== undefined ? materialTableWidth : width * 0.76
        const noteY =
          config.jplNoteY !== undefined
            ? height - config.jplNoteY
            : isBlankoLayout && materialTableBottomY !== undefined
            ? materialTableBottomY - (config.evaluationScore ? 29 : 14)
            : height - 620
        drawLeftAlignedMultiLineText(
          page,
          config.jplNote,
          font,
          noteFontSize,
          noteX,
          noteY,
          noteWidth,
          rgb(0, 0, 0),
          1.2,
        )
      }

      if (isBlankoLayout) {
        const targetQrY =
          materialTableBottomY !== undefined
            ? Math.min(92, Math.max(68, materialTableBottomY - (config.evaluationScore ? 112 : 86)))
            : qrPlacement.y
        dynamicQrPlacement = {
          ...qrPlacement,
          y: config.qrY ?? targetQrY,
        }
      }

      if (config.qrValue) {
        await drawQrCode(
          page,
          pdfDoc,
          config.qrValue,
          dynamicQrPlacement.x,
          dynamicQrPlacement.y,
          dynamicQrPlacement.size,
        )

        if (isBlankoLayout && templateConfig.branding) {
          const stampWidth = templateConfig.branding.stampWidth || 54
          const stampOffsetX = templateConfig.branding.stampOffsetX ?? 12
          drawQrBox(page, font, dynamicQrPlacement, stampWidth, stampOffsetX)
          await drawBranding(
            page,
            pdfDoc,
            width,
            height,
            { ...templateConfig.branding, logoUrl: undefined },
            dynamicQrPlacement,
          )
        }
      }

      // Add signature if provided (text wrapping mode)
      if (config.signature && !isBlankoLayout) {
        await drawSignature(page, pdfDoc, config.signature, width, height, font)
      }

      if (config.signature && isBlankoLayout) {
        const location = config.signature.location || 'Bandar Lampung'
        const dateText = resolveSignatureDate(config.signature.date)
        const locationText = `${location}, ${dateText}`
        const locationFontSize = config.signature.fontSize || 10
        const locationColor = config.signature.fontColor || { r: 0, g: 0, b: 0 }
        const locationX =
          (width - font.widthOfTextAtSize(locationText, locationFontSize)) / 2
        const locationY = dynamicQrPlacement.y + dynamicQrPlacement.size + 14

        page.drawText(locationText, {
          x: locationX,
          y: locationY,
          size: locationFontSize,
          font,
          color: rgb(
            locationColor.r / 255,
            locationColor.g / 255,
            locationColor.b / 255,
          ),
        })

        const signerName = config.signature.signerName || 'apt. Rudi Afrinanda, S.Farm'
        const signerTitle = config.signature.signerTitle || ''
        const signerFontSize = 11.5
        const signerTitleSize = 8.5
        const signerNameX =
          (width - fontBold.widthOfTextAtSize(signerName, signerFontSize)) / 2
        const signerTitleX =
          (width - font.widthOfTextAtSize(signerTitle, signerTitleSize)) / 2
        const signerBaseY = dynamicQrPlacement.y - 24

        page.drawText(signerName, {
          x: signerNameX,
          y: signerBaseY,
          size: signerFontSize,
          font: fontBold,
          color: rgb(0.15, 0.18, 0.25),
        })

        if (signerTitle) {
          page.drawText(signerTitle, {
            x: signerTitleX,
            y: signerBaseY - 14,
            size: signerTitleSize,
            font,
            color: rgb(0, 0, 0),
          })
        }
      }
    } else {
      // Fallback to original single-line text rendering
      const textWidth = font.widthOfTextAtSize(config.name, fontSize)

      // Calculate centered X position
      let x: number
      if (config.x !== undefined) {
        // If X position is explicitly set, use it
        x = config.x
      } else {
        // If no X position specified, center the text
        x = (width - textWidth) / 2
      }

      const y = height - (config.y || templateConfig.namePosition.y) // PDF coordinates are bottom-up

      // Add name text to the page
      page.drawText(config.name, {
        x,
        y,
        size: fontSize,
        font,
        color: rgb(fontColor.r / 255, fontColor.g / 255, fontColor.b / 255),
      })

      // Add course name if provided (original method)
      if (config.courseName) {
        const courseFontSize =
          config.courseFontSize || templateConfig.defaultCourseFontSize || 18
        const courseFontColor = config.courseFontColor ||
          templateConfig.defaultCourseFontColor || { r: 51, g: 51, b: 51 }

        const courseTextWidth = font.widthOfTextAtSize(
          config.courseName,
          courseFontSize,
        )

        // Calculate centered X position for course
        let courseX: number
        if (config.courseX !== undefined) {
          courseX = config.courseX
        } else {
          courseX = (width - courseTextWidth) / 2
        }

        const courseY =
          height - (config.courseY || templateConfig.coursePosition?.y || 360)

        page.drawText(config.courseName, {
          x: courseX,
          y: courseY,
          size: courseFontSize,
          font,
          color: rgb(
            courseFontColor.r / 255,
            courseFontColor.g / 255,
            courseFontColor.b / 255,
          ),
        })

        // Add topics table if provided (non-wrapping mode)
        const materialRows =
          normalizeMaterialRows(config.materials) || normalizeTopicRows(config.topics)

        if (materialRows && materialRows.length > 0) {
          const tableWidth = width * 0.8 // 80% of page width
          const tableX = width * 0.1 // Center the table (10% margin on each side)
          const tableSpacing = 30 // Space between course name and topics table
          const tableY = courseY - tableSpacing

          drawTopicsTable(
            page,
            materialRows,
            font,
            fontBold,
            tableX,
            tableY,
            tableWidth,
            12, // Table font size
            config.jplTotal,
            config.materialTopicOnly,
          )
        }
      }

      // Add signature if provided (non-wrapping mode)
      if (config.signature) {
        await drawSignature(page, pdfDoc, config.signature, width, height, font)
      }
    }

    // Save the PDF
    const pdfBytes = await pdfDoc.save()
    return pdfBytes
  } catch (error) {
    console.error('Error generating certificate:', error)
    throw error
  }
}

/**
 * Download generated certificate
 */
export const downloadGeneratedCertificate = async (
  config: CertificateConfig,
  template?: CertificateTemplate,
  customFileName?: string,
): Promise<void> => {
  try {
    const pdfBytes = await generateCertificate(config, template)

    // Create blob and download
    const blob = new Blob([pdfBytes as BlobPart], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)

    const link = document.createElement('a')
    link.href = url

    // Generate filename with current date (date only, no day name)
    const currentDate = new Date().toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
    const fileName =
      customFileName || `certificate-${config.name}-${currentDate}.pdf`

    link.download = fileName
    link.style.display = 'none'

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    // Clean up
    URL.revokeObjectURL(url)

  } catch (error) {
    console.error('Error downloading certificate:', error)
    throw error
  }
}

/**
 * Preview certificate in new window/tab
 */
export const previewCertificate = async (
  config: CertificateConfig,
  template?: CertificateTemplate,
): Promise<void> => {
  try {
    const pdfBytes = await generateCertificate(config, template)

    // Create blob and open in new window
    const blob = new Blob([pdfBytes as BlobPart], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)
    window.open(url, '_blank')

    // Clean up after a delay to ensure the window has loaded
    setTimeout(() => {
      URL.revokeObjectURL(url)
    }, 5000)

  } catch (error) {
    console.error('Error previewing certificate:', error)
    throw error
  }
}

/**
 * Get certificate as base64 string (useful for embedding in HTML)
 */
export const getCertificateAsBase64 = async (
  config: CertificateConfig,
  template?: CertificateTemplate,
): Promise<string> => {
  try {
    const pdfBytes = await generateCertificate(config, template)

    // Convert to base64 using Blob and FileReader (safer approach)
    const blob = new Blob([pdfBytes as BlobPart], { type: 'application/pdf' })

    return new Promise((resolve, reject) => {
      const reader = new FileReader()

      // Add timeout to prevent hanging
      const timeout = setTimeout(() => {
        reader.abort()
        reject(new Error('FileReader timeout'))
      }, 10000) // 10 seconds timeout

      reader.onload = () => {
        clearTimeout(timeout)
        const result = reader.result as string
        resolve(result)
      }

      reader.onerror = () => {
        clearTimeout(timeout)
        // Fallback to manual conversion if FileReader fails
        try {
          const uint8Array = new Uint8Array(pdfBytes)
          let binary = ''
          const len = uint8Array.byteLength

          // Process in smaller chunks to avoid stack overflow
          const chunkSize = 4096
          for (let i = 0; i < len; i += chunkSize) {
            const chunk = uint8Array.subarray(i, Math.min(i + chunkSize, len))
            binary += String.fromCharCode.apply(null, Array.from(chunk))
          }

          const base64 = btoa(binary)
          resolve(`data:application/pdf;base64,${base64}`)
        } catch (fallbackError) {
          reject(new Error('Failed to convert PDF to base64'))
        }
      }

      reader.readAsDataURL(blob)
    })
  } catch (error) {
    console.error('Error converting certificate to base64:', error)
    throw error
  }
}
